/**
 * Shared opportunity list card — mirrors OpportunitiesDashboard card UX
 * (deadline box, match score, Status dropdown, Summary / View / Proposal actions).
 */

import React from 'react';
import { Card, Button, Badge, formatCurrency } from '@tracekit/core';
import {
  Building2,
  Clock,
  DollarSign,
  ExternalLink,
  Eye,
  FileText,
  MapPin,
  Tag,
} from 'lucide-react';
import type { GovtOpportunityEntity } from '../../pages/government-data/OpportunitiesDashboard';
import { MatchScoreTooltip, type MatchExplain } from './MatchScoreTooltip';
import { TrackingOwnerChip } from './TrackingOwnerChip';
import {
  enhanceOpportunityEntity,
  getDeadlineUrgency,
  getOpportunityValue,
  getProjectIdDisplay,
  getStageBadgeColor,
} from '../../utils/opportunityUtils';
import { getOpportunityUrl, getTrackingOwnerName } from '../../utils/weeklyGlobalOpportunities';

export interface OpportunityDashboardCardProps {
  opportunity: GovtOpportunityEntity;
  matchScore?: number;
  onOpenDetails: (opportunity: GovtOpportunityEntity) => void;
  onUpdateTracking: (entityId: string, trackingStatus: string) => void;
  onGenerateProposal: (opportunity: GovtOpportunityEntity) => void;
  className?: string;
}

export function OpportunityDashboardCard({
  opportunity,
  matchScore,
  onOpenDetails,
  onUpdateTracking,
  onGenerateProposal,
  className,
}: OpportunityDashboardCardProps) {
  const enhancedOpp = enhanceOpportunityEntity(opportunity);
  const data = enhancedOpp.data;
  const effectiveMatchScore =
    matchScore ??
    (typeof data.match_score === 'number' && !Number.isNaN(data.match_score)
      ? data.match_score
      : undefined);
  const matchExplain =
    (data as { match_explain?: MatchExplain | null }).match_explain ?? null;
  const trackingOwner = getTrackingOwnerName(data);
  const externalUrl = getOpportunityUrl(enhancedOpp) ?? data.url ?? null;

  const daysUntil = data.days_until_deadline ?? null;
  const urgency = getDeadlineUrgency(daysUntil);
  const isUrgent = urgency === 'urgent';
  const isExpired = urgency === 'expired';
  const isSoon = urgency === 'soon';
  const opportunityValue = getOpportunityValue(data);
  const projectId = getProjectIdDisplay(enhancedOpp);
  const stage = data.proposal_stage || 'Unknown';
  const duration = data.duration_display || 'Not specified';

  const stopPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const openDetails = () => onOpenDetails(enhancedOpp);

  return (
    <div className={className}>
      <Card
        role="button"
        tabIndex={0}
        onClick={openDetails}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openDetails();
          }
        }}
        className={`h-full p-4 hover:shadow-lg transition-shadow duration-200 flex flex-col border cursor-pointer bg-white dark:bg-slate-800 dark:text-slate-100 border-theme-border focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
          data.tracking_status === 'watching'
            ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700'
            : data.tracking_status === 'bidding'
              ? 'bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-700'
              : ''
        }`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3
                className="font-semibold text-lg line-clamp-2 text-gray-900 dark:text-slate-100"
                title={data.title}
              >
                {data.title}
              </h3>
              {data.tracking_status ? (
                <Badge variant="default">{data.tracking_status}</Badge>
              ) : null}
              <TrackingOwnerChip name={trackingOwner} trackingStatus={data.tracking_status} />
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-slate-300 mb-2">
              <span className="font-mono text-xs bg-gray-100 dark:bg-slate-700 px-2 py-0.5 rounded text-gray-700 dark:text-slate-200 line-clamp-3">
                ID: {projectId}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-300 mb-2">
              <Building2 className="h-4 w-4 flex-shrink-0" />
              <span className="line-clamp-1">
                {data.organization || data.issuing_agency || 'Unknown'}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 ml-2 flex-shrink-0">
            {effectiveMatchScore != null && effectiveMatchScore > 0 ? (
              <MatchScoreTooltip
                score={Number(effectiveMatchScore)}
                variant="badge"
                explain={matchExplain}
              />
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          {stage !== 'Unknown' ? (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${getStageBadgeColor(stage)}`}
            >
              {stage}
            </span>
          ) : null}
          {opportunityValue !== null ? (
            <div className="flex items-center gap-1 bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded-md border border-green-200 dark:border-green-800">
              <DollarSign className="h-3 w-3 text-green-600 dark:text-green-400" />
              <span className="text-xs font-semibold text-green-700 dark:text-green-300">
                {formatCurrency(opportunityValue, {
                  compact: true,
                  symbol: data.currency === 'CAD' ? 'C$' : '$',
                })}
              </span>
            </div>
          ) : null}
          {duration !== 'Not specified' ? (
            <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-slate-300">
              <Clock className="h-3 w-3" />
              <span>{duration}</span>
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          <Badge
            variant={
              data.status === 'open' ? 'success' : data.status === 'closed' ? 'default' : 'warning'
            }
          >
            {data.status || 'Unknown'}
          </Badge>
          {data.source ? (
            <Badge variant="default">
              <Tag className="h-3 w-3 mr-1" />
              {data.source}
            </Badge>
          ) : null}
        </div>

        {data.deadline ? (
          <div
            className={`mb-3 p-2 rounded-lg border ${
              isUrgent
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                : isExpired
                  ? 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
                  : isSoon
                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                    : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock
                  className={`h-4 w-4 ${
                    isUrgent
                      ? 'text-red-600 dark:text-red-400'
                      : isExpired
                        ? 'text-gray-400 dark:text-slate-400'
                        : isSoon
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-blue-600 dark:text-blue-400'
                  }`}
                />
                <span className="text-xs font-medium text-gray-900 dark:text-slate-100">
                  Deadline
                </span>
              </div>
              <div className="text-right">
                <div
                  className={`text-sm font-semibold ${
                    isUrgent
                      ? 'text-red-700 dark:text-red-300'
                      : isExpired
                        ? 'text-gray-500 dark:text-gray-400'
                        : isSoon
                          ? 'text-yellow-700 dark:text-yellow-300'
                          : 'text-blue-700 dark:text-blue-300'
                  }`}
                >
                  {new Date(data.deadline).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </div>
                {daysUntil != null ? (
                  <div
                    className={`text-xs ${
                      isUrgent
                        ? 'text-red-600 dark:text-red-400'
                        : isExpired
                          ? 'text-gray-500 dark:text-gray-400'
                          : isSoon
                            ? 'text-yellow-600 dark:text-yellow-400'
                            : 'text-blue-600 dark:text-blue-400'
                    }`}
                  >
                    {isExpired
                      ? `Expired ${Math.abs(daysUntil)} days ago`
                      : daysUntil === 0
                        ? 'Due today!'
                        : daysUntil === 1
                          ? '1 day left'
                          : `${daysUntil} days left`}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {data.ai_card_summary || data.description ? (
          <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-3 line-clamp-2">
            {data.ai_card_summary || data.description}
          </p>
        ) : null}

        {data.location ? (
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-300 mb-3">
            <MapPin className="h-4 w-4 flex-shrink-0" />
            <span className="line-clamp-1">{data.location}</span>
          </div>
        ) : null}

        <div className="mb-3" onClick={stopPropagation}>
          <label className="block text-xs font-medium text-gray-700 dark:text-slate-200 mb-1">
            Status
          </label>
          <select
            value={data.tracking_status || ''}
            onChange={(event) => onUpdateTracking(enhancedOpp.id, event.target.value)}
            onClick={stopPropagation}
            className="w-full px-2 py-1 text-sm border border-theme-border rounded bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none"
          >
            <option value="">Not tracking</option>
            <option value="watching">Watching</option>
            <option value="bidding">Bidding</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
            <option value="declined">Declined</option>
          </select>
        </div>

        <div className="flex gap-2 mt-auto" onClick={stopPropagation}>
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={(event) => {
              stopPropagation(event);
              openDetails();
            }}
          >
            <Eye className="h-4 w-4 mr-1" />
            Summary
          </Button>
          {externalUrl ? (
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={(event) => {
                stopPropagation(event);
                window.open(externalUrl, '_blank', 'noopener,noreferrer');
              }}
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              View
            </Button>
          ) : null}
          <Button
            variant="primary"
            size="sm"
            className="flex-1"
            onClick={(event) => {
              stopPropagation(event);
              onGenerateProposal(enhancedOpp);
            }}
          >
            <FileText className="h-4 w-4 mr-1" />
            Proposal
          </Button>
        </div>
      </Card>
    </div>
  );
}
